import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Updater } from "use-immer";

import {
  IgButton,
  IgPaper,
  IgSelect,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  Close,
  KeyboardArrowDown,
  KeyboardArrowUp,
  SwapHoriz,
} from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  MenuItem,
  Popover,
  SelectChangeEvent,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { useConfiguration } from "../../../api/configurations/use-configuration";
import { useIsAdmin } from "../../../api/user-auths/is-admin";
import {
  setDashboardFilter,
  updateDashboardFilter,
} from "../../../store/slices/dashboardFilters";
import { IExperienceDashboardFilters } from "../../../store/types/dashboardFilters";
import { IAppState } from "../../../store/types/store";
import { VariationFe } from "../../../types/schemas/variations";
import { AiAnalysisButton } from "../aiSection/components/AiAnalysisButton";

interface CompareVariationsProps {
  variations: VariationFe[];
  compareVariationsAnchorEl: HTMLButtonElement | null;
  setCompareVariationsAnchorEl: Updater<HTMLButtonElement | null>;
}

const PREFIX = "CompareVariations";

const classes = {
  compareButton: `${PREFIX}-compareButton`,
  groupName: `${PREFIX}-groupName`,
  groupNameDivider: `${PREFIX}-groupNameDivider`,
  formInputBox: `${PREFIX}-textfieldBox`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .${classes.compareButton}`]: {
    color: theme.palette.gray.main,
    border: `1px solid ${theme.palette.disabled.main}`,
    backgroundColor: "white",
    height: "37px",
    "&:hover": {
      border: `1px solid ${theme.palette.primary.main}`,
      backgroundColor: `rgba(41, 98, 255, 0.04)`,
    },
  },
  [`& .${classes.groupName}`]: {
    fontSize: "14px",
    maxWidth: "70px",
  },
  [`& .${classes.groupNameDivider}`]: {
    fontSize: "14px",
    paddingRight: "5px",
    paddingLeft: "5px",
  },
  [`& .${classes.formInputBox}`]: {
    minWidth: "150px",
  },
}));

export const CompareVariations = ({
  variations,
  compareVariationsAnchorEl,
  setCompareVariationsAnchorEl,
}: CompareVariationsProps) => {
  const isAdmin = useIsAdmin();
  const enableAi = useConfiguration<boolean>("enable_ai")?.value;

  const hasAiAccess = isAdmin || enableAi;

  const dashboardFilters = useSelector(
    (state: IAppState) => state.dashboardFilters
  );
  const dispatch = useDispatch();

  const onGroupSwap = (currGroupA: string, currGroupB: string) => {
    dispatch(
      setDashboardFilter({
        ...dashboardFilters,
        compareGroupA: currGroupB,
        compareGroupB: currGroupA,
      })
    );
  };

  const onGroupChange = (
    key: keyof IExperienceDashboardFilters,
    value: string
  ) => {
    dispatch(
      updateDashboardFilter({
        key,
        value,
      })
    );
  };

  return (
    <StyledGrid container item columnSpacing={1}>
      <Grid item>
        <IgButton
          onClick={(e: any) => {
            setCompareVariationsAnchorEl(e.currentTarget);
          }}
          endIcon={
            compareVariationsAnchorEl ? (
              <KeyboardArrowUp />
            ) : (
              <KeyboardArrowDown />
            )
          }
          className={classes.compareButton}
          variant={"outlined"}
        >
          <>
            <IgTypography noWrap={true} className={classes.groupName}>
              {variations.find((v) => v.id === dashboardFilters.compareGroupA)
                ?.name || ""}
            </IgTypography>
            <IgTypography className={classes.groupNameDivider}>vs</IgTypography>
            <IgTypography noWrap={true} className={classes.groupName}>
              {variations.find((v) => v.id === dashboardFilters.compareGroupB)
                ?.name || ""}
            </IgTypography>
          </>
        </IgButton>
        <Popover
          sx={{ marginTop: "8px" }}
          slotProps={{
            paper: { elevation: 0, style: { borderRadius: 15 } },
          }}
          id={"popover"}
          open={Boolean(compareVariationsAnchorEl)}
          anchorEl={compareVariationsAnchorEl}
          onClose={() => {
            setCompareVariationsAnchorEl(null);
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <IgPaper sx={{ padding: "15px" }}>
            <Grid item container direction={"column"} rowSpacing={1}>
              <Grid
                item
                container
                justifyContent={"space-between"}
                direction={"row"}
              >
                <Grid item>
                  <IgTypography>Compare any 2 groups</IgTypography>
                </Grid>
                <Grid item>
                  <IconButton
                    sx={{ padding: "0px", color: "rgba(0, 0, 0, 0.38)" }}
                    size={"small"}
                    onClick={() => {
                      setCompareVariationsAnchorEl(null);
                    }}
                  >
                    <Close />
                  </IconButton>
                </Grid>
              </Grid>
              <Grid container item direction={"row"} columnSpacing={1}>
                <Grid item>
                  <Box sx={{ minWidth: "150px" }}>
                    <IgSelect
                      autoWidth={true}
                      fullWidth={true}
                      labelId="groupA-select-label"
                      id="groupA-select"
                      value={dashboardFilters.compareGroupA}
                      onChange={(e: SelectChangeEvent<any>) => {
                        onGroupChange("compareGroupA", e.target.value);
                      }}
                    >
                      {variations
                        .filter(
                          (group) => group.id !== dashboardFilters.compareGroupB
                        )
                        .map((group: VariationFe) => (
                          <MenuItem key={group.id} value={group.id}>
                            {group.name}
                          </MenuItem>
                        ))}
                    </IgSelect>
                  </Box>
                </Grid>
                <Grid item>
                  <IconButton
                    onClick={() => {
                      onGroupSwap(
                        dashboardFilters.compareGroupA!,
                        dashboardFilters.compareGroupB!
                      );
                    }}
                  >
                    <SwapHoriz />
                  </IconButton>
                </Grid>
                <Grid item>
                  <Box sx={{ minWidth: "150px" }}>
                    <IgSelect
                      fullWidth={true}
                      autoWidth={true}
                      labelId="groupB-select-label"
                      id="groupB-select"
                      value={dashboardFilters.compareGroupB}
                      label="Select Group"
                      onChange={(e: SelectChangeEvent<any>) => {
                        onGroupChange("compareGroupB", e.target.value);
                      }}
                    >
                      {variations
                        .filter(
                          (group) => group.id !== dashboardFilters.compareGroupA
                        )
                        .map((group: VariationFe) => (
                          <MenuItem key={group.id} value={group.id}>
                            {group.name}
                          </MenuItem>
                        ))}
                    </IgSelect>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </IgPaper>
        </Popover>
      </Grid>
      {hasAiAccess ? (
        <Grid item>
          <AiAnalysisButton />
        </Grid>
      ) : null}
    </StyledGrid>
  );
};
